import { BreakpointObserver } from "@angular/cdk/layout";
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatStepper } from "@angular/material/stepper";
import { NavigationEnd, Router } from "@angular/router";
import { Tools } from "@comunicare/comunicare-helper";
import { FORMS_MODE, IPatient, IPatientUser, IPrescription, IRelatedPerson } from "@comunicare/comunicare-interfaces";
import { TranslateService } from "@ngx-translate/core";
import moment from "moment";
import { fromEvent, of, Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, filter, first, map, switchMap, takeUntil } from "rxjs/operators";
import { PatientService } from "src/app/providers/patient.service";
import { PatientRegistrationService } from "src/app/providers/patientRegistration.service";
import { RelatedService } from "src/app/providers/related.service";
import { UserService } from "src/app/providers/user.service";

@Component({
  selector: "app-patient-registration-page",
  templateUrl: "./patient-registration-page.component.html",
  styleUrls: ["./patient-registration-page.component.scss"],
})
export class PatientRegistrationPageComponent implements OnInit, OnDestroy, AfterViewInit {
  /** Emits when the component is destroyed, used to complete all active subscriptions. */
  private onDestroy$ = new Subject<void>();

  /**
   * Emits when the component is re-initialized mid-lifecycle (e.g. navigating back to patient
   * creation while already inside the patientRegistration flow). Used to cancel subscriptions
   * created in {@link initFromNavState} without destroying the component itself.
   */
  private onReinit$ = new Subject<void>();

  public stepperOrientation;
  public isLoading = true;
  public isCreation = true;
  public patient: IPatientUser;
  public source: string;
  public linkData: {
    name: string;
    value: string | number;
  }[];
  public timeline: { id: string; title: string; subtitles: string[] }[] = [];
  public go2patientPage: boolean;
  public relateds: IRelatedPerson[] = [];
  private directToCareplan: boolean;
  public addRelatedToExistingPatient = false;
  public isPatientUnder18: boolean;
  private directToPrescription: boolean;
  private timelineIds = {
    patient: "patientRegistration",
    relateds: "relatedRegistration",
    careplan: "careplan",
    vitalSigns: "vitalSigns",
    observation: "observationDefinitions",
    prescription: "prescription",
  };

  /** Navigation state captured in the constructor while {@link Router.getCurrentNavigation} is still valid. */
  private navState: any;

  /** Tracks the currently active child route URL to detect intra-flow navigations. */
  private currentChildRoute: string;

  @ViewChild("stepper") public stepper: MatStepper;

  constructor(
    private router: Router,
    private translateService: TranslateService,
    private relatedService: RelatedService,
    public patientRegistrationService: PatientRegistrationService,
    private patientService: PatientService,
    private cdr: ChangeDetectorRef,
    private userService: UserService
  ) {
    // getCurrentNavigation() is only valid during the navigation event, so the state
    // must be captured immediately in the constructor before it becomes null.
    const nav = this.router.getCurrentNavigation();
    this.navState = nav?.extras?.state ?? {};
    this.currentChildRoute = this.router.url;

    // Detect a browser refresh on a stepper step other than patient creation.
    // On refresh, history.state loses all navigation data, leaving an empty object
    // (or only navigationId). If we're past the /patient entry point without any
    // state, we can't reconstruct the context — redirect to home.
    const isOnChildRoute = this.router.url.includes("patientRegistration/") && !this.router.url.includes("patientRegistration/patient");
    const hasNoState =
      !this.navState?.directToCareplan &&
      !this.navState?.directToPrescription &&
      !Tools.isDefined(this.navState?.mode) &&
      !Tools.isDefined(this.navState?.patient);

    if (isOnChildRoute && hasNoState) {
      this.router.navigateByUrl("/");
      return;
    }

    // previousUrl resolution requires the router events stream and must stay in the constructor.
    this.router.events
      .pipe(
        first(),
        map(() => this.router.getCurrentNavigation().previousNavigation?.finalUrl?.toString())
      )
      .subscribe((previousUrl) => {
        this.patientRegistrationService.previousUrl = previousUrl;
      });

    const breakpointObserver = inject(BreakpointObserver);
    const host: HTMLElement = inject(ElementRef).nativeElement; // Switching orientation re-renders the whole step
    // So we wait until no field inside this page is focused before applying the new orientation.
    const isEditing = () => {
      const active = document.activeElement;
      return !!active && host.contains(active) && active.matches("input, textarea, select, [contenteditable]");
    };
    this.stepperOrientation = breakpointObserver.observe("(min-width: 800px)").pipe(
      map(({ matches }) => (matches ? "horizontal" : "vertical")),
      switchMap((orientation) =>
        isEditing()
          ? fromEvent(document, "focusout").pipe(
              // let focus settle: moving to another field must keep the orientation frozen
              debounceTime(0),
              filter(() => !isEditing()),
              first(),
              map(() => orientation)
            )
          : of(orientation)
      ),
      distinctUntilChanged()
    );
  }

  ngOnInit(): void {
    this.patientRegistrationService.reset();
    this.initFromNavState(this.navState);

    // Angular reuses this component instance when navigating between child routes
    // (e.g. patientRegistration/careplan -> patientRegistration/patient), so ngOnInit
    // is never called again. We listen to NavigationEnd to detect when the user
    // navigates back to the patient creation entry point and manually re-initialize.
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.onDestroy$)
      )
      .subscribe((event: NavigationEnd) => {
        const goingBackToPatient = event.urlAfterRedirects.includes("patientRegistration/patient");
        const wasOnPatientAlready = this.currentChildRoute?.includes("patientRegistration/patient");

        if (goingBackToPatient && !wasOnPatientAlready) {
          // Signal any subscriptions from the previous initFromNavState call to unsubscribe
          // before re-initializing, to avoid duplicate event handlers.
          this.onReinit$.next();
          this.patientRegistrationService.reset();
          this.initFromNavState(history.state);
          if (this.stepper) {
            this.stepper.reset();
          }
        }

        this.currentChildRoute = event.urlAfterRedirects;
      });
  }

  ngAfterViewInit(): void {
    if (this.directToCareplan || this.directToPrescription) {
      this.stepper.next();
      if (this.directToPrescription) {
        this.stepper.next();
      }
      this.calculateLastStep();
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
    this.onReinit$.complete();
  }

  /**
   * Initializes the component state from a navigation state object.
   *
   * This method is called both on first load (from {@link ngOnInit} via {@link navState})
   * and on subsequent intra-flow navigations back to the patient creation entry point
   * (from the {@link NavigationEnd} listener via {@link history.state}).
   *
   * It resets local and service state, rebuilds the stepper timeline, and sets up
   * the appropriate RxJS subscriptions for the current use case
   * (new patient, direct-to-careplan, or direct-to-prescription).
   *
   * @param navState - The navigation extras state object passed via {@link Router.navigate}.
   */
  private initFromNavState(navState: any): void {
    this.isCreation = navState?.mode !== FORMS_MODE.UPDATE;
    this.patient = navState?.patient || false;
    this.source = navState?.source;

    if (navState?.linkData?.length) {
      this.linkData = navState.linkData;
      this.patientRegistrationService.linkData = this.linkData;
    }

    this.directToCareplan = navState?.directToCareplan || false;
    if (this.directToCareplan) {
      this.patientRegistrationService.patientUser = navState?.patientUser;
      this.patientRegistrationService.careplan = navState?.careplan;
      this.patientRegistrationService.careplans = navState?.careplans;
      this.patientRegistrationService.createNewCareplan = this.patientRegistrationService.careplan === undefined;
      this.patientRegistrationService.includeBirthdateAndGenderForm = navState?.includeBirthdateAndGenderForm;
      this.patientRegistrationService.registration = navState?.registration;
      this.patientRegistrationService.healthcareService = navState?.healthcareService;
    }

    this.directToPrescription = navState?.directToPrescription || false;
    if (this.directToPrescription) {
      this.patientRegistrationService.patientUser = navState?.patientUser;
      this.patientRegistrationService.careplan = navState?.careplan;
      this.patientRegistrationService.careplanPrescriptions = navState?.careplanPrescriptions;
      this.patientRegistrationService.careplanRef = navState?.careplanRef;
      this.patientRegistrationService.prescription = navState?.prescription;
      this.patientRegistrationService.prescriptionConfig = navState?.prescriptionConfig;
      this.patientRegistrationService.isPendingPrescriptionEdit = navState?.isPendingPrescriptionEdit;
    }

    // Use takeUntil(onReinit$) in addition to takeUntil(onDestroy$) so that these
    // subscriptions are cancelled if initFromNavState is called again mid-lifecycle,
    // preventing duplicate handlers from accumulating across re-initializations.
    const untilReinitOrDestroy$ = this.onReinit$.pipe(takeUntil(this.onDestroy$));

    if (this.isCreation) {
      this.timeline = [
        {
          id: this.timelineIds.patient,
          title: "page.careplan.patient",
          subtitles: [],
        },
        {
          id: this.timelineIds.careplan,
          title: "page.careplan.title",
          subtitles: [],
        },
      ];

      if (!this.directToCareplan && !this.directToPrescription) {
        this.patientRegistrationService.onPatientCreated$
          .pipe(takeUntil(untilReinitOrDestroy$))
          .subscribe((event: { patientSaved: IPatient; forceUpdateUser?: string }) => {
            this.patientCreated(event);
          });
        this.patientRegistrationService.onRelatedCreated$.pipe(takeUntil(untilReinitOrDestroy$)).subscribe((event: IRelatedPerson) => {
          this.relatedCreated(event);
        });
        this.patientRegistrationService.onNeedReferentChanged$.pipe(takeUntil(untilReinitOrDestroy$)).subscribe((event: boolean) => {
          this.timelineWithReferent(event);
        });
        this.patientRegistrationService.onNeedCareplanChanged$.pipe(takeUntil(untilReinitOrDestroy$)).subscribe((event: boolean) => {
          this.timelineWithCareplan(event);
        });

        this.patientRegistrationService.isCreation = this.isCreation;
        this.patientRegistrationService.source = this.source;
      } else {
        this.timeline[0].subtitles.push(
          "✓ " +
            this.patientRegistrationService.patientUser?.patient.name[0].given +
            " " +
            this.patientRegistrationService.patientUser?.patient.name[0].family
        );
        this.patientRegistrationService.directToCareplan = this.directToCareplan;

        if (this.directToPrescription) {
          if (this.patientRegistrationService.careplan?.description || this.patientRegistrationService.careplanRef?.display) {
            this.timeline[1].subtitles.push(
              "✓ " + (this.patientRegistrationService.careplan?.description ?? this.patientRegistrationService.careplanRef.display)
            );
          }
          this.timeline.push({
            id: this.timelineIds.prescription,
            title: "page.prescription.title",
            subtitles: [],
          });
          this.patientRegistrationService.directToPrescription = this.directToPrescription;
        }
      }

      this.patientRegistrationService.onNextStep$.pipe(takeUntil(untilReinitOrDestroy$)).subscribe(() => {
        this.onNext();
      });
      this.patientRegistrationService.onNeedPrescriptionChanged$.pipe(takeUntil(untilReinitOrDestroy$)).subscribe((event: boolean) => {
        this.timelineWithPrescription(event);
      });
      this.patientRegistrationService.onCareplanCreatedOrUpdated$.pipe(takeUntil(untilReinitOrDestroy$)).subscribe((event: string) => {
        this.careplanCreatedOrUpdated(event);
      });
      this.patientRegistrationService.onPrescriptionCreated$.pipe(takeUntil(untilReinitOrDestroy$)).subscribe((event: IPrescription) => {
        this.prescriptionCreated(event);
      });

      this.isLoading = false;
    } else {
      this.isPatientUnder18 = moment(this.patient.user.birthdate).isAfter(moment().subtract(18, "y"));
      this.relatedService
        .getPatientRelateds(this.patient.user.caremateIdentifier)
        .pipe(first())
        .subscribe((relateds) => {
          if (relateds?.length) {
            this.relateds = relateds;
          }
        });
    }
    this.patientRegistrationService.onNeedVitalSignsChanged$.pipe(takeUntil(untilReinitOrDestroy$)).subscribe((event: boolean) => {
      this.timelineWithVitalSigns(event);
    });
  }

  /**
   * Handles the {@link PatientRegistrationService.onPatientCreated$} event.
   * Stores the saved patient in the service and appends a confirmation subtitle
   * to the patient step in the timeline.
   *
   * @param event - Contains the saved {@link IPatient} and an optional flag to force a user update.
   */
  private patientCreated(event: { patientSaved: IPatient; forceUpdateUser?: string }): void {
    this.patientRegistrationService.patientSaved = event.patientSaved;
    this.patientRegistrationService.isForceUpdateUser = Tools.isDefined(event.forceUpdateUser);
    const patientStep = this.timeline.find((step) => step.id === this.timelineIds.patient);
    patientStep.subtitles.push("✓ " + event.patientSaved.name[0].given + " " + event.patientSaved.name[0].family);
  }

  /**
   * Adds or removes the referent (related person) step from the stepper timeline
   * in response to the {@link PatientRegistrationService.onNeedReferentChanged$} event.
   * The referent step is always inserted at position 1 (right after the patient step).
   *
   * @param needReferent - Whether the referent step should be present in the timeline.
   */
  private timelineWithReferent(needReferent: boolean): void {
    const timelineReferentIndex = this.timeline.findIndex((step) => step.id === this.timelineIds.relateds);
    if (needReferent && timelineReferentIndex === -1) {
      this.timeline.splice(1, 0, {
        id: this.timelineIds.relateds,
        title: "forms.createRelatedTitle",
        subtitles: [],
      });
    }
    if (!needReferent && timelineReferentIndex !== -1) {
      this.timeline.splice(timelineReferentIndex, 1);
    }
    this.calculateLastStep();
  }

  /**
   * Adds or removes the careplan, vital signs, and observation definition steps
   * from the stepper timeline in response to the {@link PatientRegistrationService.onNeedCareplanChanged$} event.
   *
   * @param needCareplan - Whether the careplan-related steps should be present in the timeline.
   */
  private timelineWithCareplan(needCareplan: boolean): void {
    const timelineCareplanIndex = this.timeline.findIndex((step) => step.id === this.timelineIds.careplan);
    if (needCareplan && timelineCareplanIndex === -1) {
      this.timeline.push({
        id: this.timelineIds.careplan,
        title: "page.careplan.title",
        subtitles: [],
      });
    }
    if (!needCareplan && timelineCareplanIndex !== -1) {
      this.deleteFromTimeline([this.timelineIds.careplan, this.timelineIds.observation]);
    }
    this.calculateLastStep();
  }

  private timelineWithVitalSigns(needVitalSigns: boolean): void {
    const timelineVitalSignsIndex = this.timeline.findIndex((step) => step.id === this.timelineIds.vitalSigns);
    const timelineCareplanIndex = this.timeline.findIndex((step) => step.id === this.timelineIds.careplan);

    if (needVitalSigns && timelineCareplanIndex !== -1 && timelineVitalSignsIndex === -1) {
      this.timeline.splice(timelineCareplanIndex + 1, 0, {
        id: this.timelineIds.vitalSigns,
        title: "page.careplanEditor.tabs.vitalSigns.name",
        subtitles: [],
      });
      if (this.userService.isAuthorizedSync(null, "dashboard/configurationPatient", "PUT")) {
        this.timeline.splice(timelineCareplanIndex + 2, 0, {
          id: this.timelineIds.observation,
          title: "observation.title",
          subtitles: [],
        });
      }
    }
    if (!needVitalSigns && timelineVitalSignsIndex !== -1) {
      this.deleteFromTimeline([this.timelineIds.vitalSigns]);
      this.deleteFromTimeline([this.timelineIds.observation]);
    }
    this.calculateLastStep();
  }

  /**
   * Adds or removes the prescription step from the stepper timeline
   * in response to the {@link PatientRegistrationService.onNeedPrescriptionChanged$} event.
   *
   * @param prescription - Whether the prescription step should be present in the timeline.
   */
  private timelineWithPrescription(prescription: boolean): void {
    const timelinePrescriptionIndex = this.timeline.findIndex((step) => step.id === this.timelineIds.prescription);
    if (prescription && timelinePrescriptionIndex === -1) {
      this.timeline.push({
        id: this.timelineIds.prescription,
        title: "page.prescription.title",
        subtitles: [],
      });
    }
    if (!prescription && timelinePrescriptionIndex !== -1) {
      this.timeline.splice(timelinePrescriptionIndex, 1);
    }
    this.calculateLastStep();
  }

  /**
   * Handles the {@link PatientRegistrationService.onRelatedCreated$} event.
   * Appends a confirmation subtitle to the referent step in the timeline.
   *
   * @param event - The newly created {@link IRelatedPerson}.
   */
  private relatedCreated(event: IRelatedPerson): void {
    const relatedStep = this.timeline.find((step) => step.id === this.timelineIds.relateds);
    relatedStep.subtitles.push("✓ " + event.name.given.join(" ") + " " + event.name.family.join(" "));
  }

  /**
   * Handles the {@link PatientRegistrationService.onCareplanCreatedOrUpdated$} event.
   * Appends a confirmation subtitle to the careplan step in the timeline.
   *
   * @param event - The description/title of the careplan that was created or updated.
   */
  private careplanCreatedOrUpdated(event: string): void {
    const careplanStep = this.timeline.find((step) => step.id === this.timelineIds.careplan);
    careplanStep.subtitles.push("✓ " + event);
  }

  /**
   * Handles the {@link PatientRegistrationService.onPrescriptionCreated$} event.
   * Appends a confirmation subtitle to the prescription step in the timeline.
   *
   * @param event - The newly created {@link IPrescription}.
   */
  private prescriptionCreated(event: IPrescription): void {
    const prescriptionStep = this.timeline.find((step) => step.id === this.timelineIds.prescription);
    prescriptionStep.subtitles.push("✓ " + this.translateService.instant(event.category?.coding?.[0]?.display));
  }

  /**
   * Advances the stepper to the next relevant step and navigates to the corresponding child route.
   *
   * The next step index is computed from the current stepper position, with adjustments for:
   * - The absence of the referent step in the timeline (index is incremented to skip the form).
   * - The user lacking the observation definition edit permission (index is incremented to skip that step).
   *
   * Triggered by the {@link PatientRegistrationService.onNextStep$} event emitted by child components.
   */
  public onNext(): void {
    let indexNextStep: number = this.stepper.selectedIndex + 1;
    if (this.timeline.findIndex((step) => step.id === this.timelineIds.relateds) === -1) {
      // No related step in timeline, increment the index to skip the form.
      indexNextStep++;
    }
    const patientSaved = this.patientRegistrationService.patientSaved;
    if (indexNextStep === 3 && this.timeline.findIndex((step) => step.id === this.timelineIds.vitalSigns) === -1) {
      // No vitalSigns step in timeline, increment the index to skip the form.
      indexNextStep++;
    }
    if (
      indexNextStep === 4 &&
      (!this.userService.isAuthorizedSync(null, "dashboard/configurationPatient", "PUT") ||
        this.timeline.findIndex((step) => step.id === this.timelineIds.observation) === -1)
    ) {
      // No observations step in timeline (or not authorized), skip that step.
      indexNextStep++;
    }

    switch (indexNextStep) {
      case 1:
        this.router.navigate(["patientRegistration", "relateds"]);
        this.stepper.next();
        this.calculateLastStep();
        break;
      case 2: {
        this.isLoading = true;
        const id = this.patientService.getComunicareId(patientSaved);
        this.patientService
          .getPatientUser(id)
          .pipe(first())
          .subscribe((patientUser) => {
            this.patientRegistrationService.patientUser = patientUser;
            this.patientRegistrationService.registration = true;
            this.patientRegistrationService.healthcareService = patientSaved.healthcareservice[patientSaved.healthcareservice.length - 1];
            this.router.navigate(["patientRegistration", "careplan"]);
            this.isLoading = false;
            this.stepper.next();
            this.calculateLastStep();
          });
        break;
      }
      case 3:
        this.router.navigate(["patientRegistration", "vitalSigns"]);
        this.stepper.next();
        this.calculateLastStep();
        break;
      case 4:
        this.router.navigate(["patientRegistration", "observationDefinitions"]);
        this.stepper.next();
        this.calculateLastStep();
        break;
      case 5:
        this.router.navigate(["patientRegistration", "prescription"]);
        this.stepper.next();
        this.calculateLastStep();
        break;
    }
  }

  /**
   * Updates the {@link PatientRegistrationService.isLastStep} flag based on
   * whether the stepper is currently on its last step.
   * Called after every stepper navigation or timeline mutation.
   */
  private calculateLastStep(): void {
    this.patientRegistrationService.isLastStep = this.timeline.length - 1 === this.stepper.selectedIndex;
  }

  /**
   * Callback invoked when a related person has been successfully added to an existing patient
   * outside of the registration flow. Closes the inline related form and appends the new
   * related person to the local list.
   *
   * @param related - The {@link IRelatedPerson} that was just created.
   */
  public referentAddedToPatient(related: IRelatedPerson): void {
    this.addRelatedToExistingPatient = false;
    this.relateds.push(related);
  }

  /** Toggles the visibility of the inline add-related form for an existing patient. */
  public addRelated(): void {
    this.addRelatedToExistingPatient = !this.addRelatedToExistingPatient;
    this.cdr.detectChanges();
  }

  /**
   * Removes multiple steps from the timeline by their IDs.
   * Steps not present in the timeline are silently ignored.
   *
   * @param ids - Array of timeline step IDs to remove.
   */
  private deleteFromTimeline(ids: string[]): void {
    ids.forEach((id) => {
      const index = this.timeline.findIndex((t) => t.id === id);
      if (index !== -1) {
        this.timeline.splice(index, 1);
      }
    });
  }
}
